import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingCb, setPendingCb] = useState(null);

  useEffect(() => {
    const init = async () => {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  // Open the login modal; optional callback runs after successful login.
  const promptLogin = useCallback((cb) => {
    setPendingCb(() => cb || null);
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => setLoginOpen(false), []);

  const onLoginSuccess = useCallback(
    (loggedUser) => {
      setLoginOpen(false);
      const cb = pendingCb;
      setPendingCb(null);
      if (cb) cb(loggedUser);
    },
    [pendingCb]
  );

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, loginOpen, promptLogin, closeLogin, onLoginSuccess }}
    >
      {children}
    </AuthContext.Provider>
  );
}
